import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ngHelper } from '@cakeiteasy/web-utils';
import { TranslateService } from '@ngx-translate/core';

import { IBakeryDeliveryInfo } from '../../models/i-bakery-delivery.info';
import { UI_DELIVERY_OPTIONS } from '../../../cart/constants/common';
import { IOverwritingDelivery } from '../../../cart';
import { getDeliveryOptions } from '../../delivery-helper';

export interface IDeliveryOption {
  icon: string;
  title: string;
  info: string;
  type: UI_DELIVERY_OPTIONS;
}

@Component({
  standalone: false,
  selector: 'choose-delivery-type',
  templateUrl: './choose-delivery-type.component.html',
  styleUrls: ['./choose-delivery-type.component.scss']
})
export class ChooseDeliveryTypeComponent implements OnChanges {
  @Input() overwritingDelivery: IOverwritingDelivery;
  @Input() isGiftDelivery = false;
  @Input() currency = 'kr';
  @Input() deliveryInfo: IBakeryDeliveryInfo;
  @Input() blockedOptions: string[];
  @Input() btnColor: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary' = 'outline-primary';
  @Input() showHeader = true;
  @Input() showToDoorDescription = true;

  @Input()
  public get deliveryType(): UI_DELIVERY_OPTIONS {
    return this.type;
  }

  public set deliveryType(type: UI_DELIVERY_OPTIONS) {
    this.type = type;
    this.deliveryTypeChange.emit(type);
  }

  @Output() deliveryTypeChange = new EventEmitter<UI_DELIVERY_OPTIONS>();

  public type: UI_DELIVERY_OPTIONS;
  public deliveryOptions: IDeliveryOption[];
  public title: string;
  public extraBtnClasses = '';

  constructor(private _translateService: TranslateService) {
    this.deliveryOptions = []; // do based on IBakeryDeliveryInfo
  }

  public isBlocked(type: UI_DELIVERY_OPTIONS) {
    return this.blockedOptions && this.blockedOptions.indexOf(type) !== -1;
  }

  public updateDeliveryType(type: UI_DELIVERY_OPTIONS) {
    if (this.isBlocked(type)) {
      return;
    }

    this.deliveryType = type;
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'deliveryInfo', () => {
      this._init();
    });

    ngHelper.ifChanged(changes, 'blockedOptions', () => {
      this._init();
    });
  }

  private _init() {
    this.deliveryOptions = this._getAvailableOptions();
    this.title = this.showHeader ? this._getTitle() : '';
    this.extraBtnClasses = this.deliveryOptions.length === 2 ? '' : 'small';
  }

  private _getTitle() {
    const { PICKUP, DELIVERY } = UI_DELIVERY_OPTIONS;
    const pickupAndToDoor = this.deliveryOptions
      .filter(({ type }) => type === PICKUP || type === DELIVERY);

    if (pickupAndToDoor.length === 2) {
      return this._translate('header');
    }

    return this._translate('chooseOptionsHeader');
  }

  private _translate(key: string, params = {}): string {
    return this._translateService.instant('orderProcess.delivery.chooseDeliveryType.' + key, params);
  }

  private _getAvailableOptions(): IDeliveryOption[] {
    const { outletsExists, mailDeliveryPointsExists, deliveryPointsExists, thirdPartyDeliveryAllowed } = this.deliveryInfo;
    const params = {
      outletsExists,
      deliveryPointsExists,
      thirdPartyDeliveryAllowed,
      isGiftDelivery: this.isGiftDelivery,
      mailDeliveryPointsExists: mailDeliveryPointsExists && !this.isBlocked(UI_DELIVERY_OPTIONS.MAIL),
    };

    return getDeliveryOptions(params)
      .reduce((acc, variant) => (acc.push(this._getOptions(variant)), acc), []);
  }

  private _getOptions(key: UI_DELIVERY_OPTIONS) {
    const { PICKUP, DELIVERY, MAIL, GIFT } = UI_DELIVERY_OPTIONS;
    const { outletsCount } = this.deliveryInfo;

    const options = {
      [PICKUP]: {
        title: this._translate('pickUpInStore'),
        icon: 'icon-map-marker',
        type: UI_DELIVERY_OPTIONS.PICKUP,
        info: `${outletsCount} ${this._translate('pickupPoints')}`,
      },
      [DELIVERY]: {
        title: this._translate('homeDelivery'),
        icon: 'icon-truck-delivery',
        type: UI_DELIVERY_OPTIONS.DELIVERY,
        info: null,
      },
      [GIFT]: {
        title: this._translate('giftDelivery.title'),
        icon: null,
        type: UI_DELIVERY_OPTIONS.GIFT,
        info: null,
      },
      [MAIL]: {
        title: this._translate('mailDelivery.title'),
        icon: 'icon-truck-delivery',
        type: UI_DELIVERY_OPTIONS.MAIL,
        info: null,
      }
    };

    return options[key];
  }
}
